import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AdStatsDiscrepanciesService } from '../../../../../shared/services/ad-stats-discrepancies.service';
import { GlobalSpinnerService } from '../../../../../shared/components/loader-service/global-spinner.service';
import { DiscrepanciesData } from '../../../domain/discrepancies-data.model';
import { MomentDateUtils } from '../../../../../shared/utils/moment-date-utils';
import { DateRangeType } from '../../../../../shared/domain/date-range-type.enum';
import { DateRangeHolder } from '../../../../../shared/domain/date-range-holder.model';
import { AutoUnsubscribeOnDestroy } from '../../../../../shared/utils/auto-unsubscribe.decorator';
import { DiscrepanciesRequest, DiscrepancyReportRequestDataSource } from '../../../domain/discrepancies-request.model';

@Component({
  selector: 'cm-kn-main-table-discrepancies-dialog',
  templateUrl: './kn-main-table-discrepancies-dialog.component.html',
  styleUrls: ['./kn-main-table-discrepancies-dialog.component.scss']
})
@AutoUnsubscribeOnDestroy()
export class KnMainTableDiscrepanciesDialogComponent implements OnInit {
  @ViewChild(MatSort, {static: true}) sort: MatSort;
   @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  dateFromPickerSubj;
  dateToPickerSubj;

  loading = true;
  activeUnitsFilter = false;
  dataSource = new MatTableDataSource<DiscrepanciesData>();

  columns = [
    {key: 'demandPartner', header: 'Demand partner'},
    {key: 'dfpRevenue', header: 'DFP revenue'},
    {key: 'dpRevenue', header: 'Partner revenue'},
    {key: 'revenueDiscrepancy', header: '% DIF'},
    {key: 'dfpImpressions', header: 'DFP filled IMPS'},
    {key: 'dpImpressions', header: 'Partner filled IMPS'},
    {key: 'impressionsDiscrepancy', header: '% DIF'},
    {key: 'dfpAdRequests', header: 'DFP requests'},
    {key: 'dpAdRequests', header: 'Partner requests'},
    {key: 'adRequestsDiscrepancy', header: '% DIF'},
    {key: 'dfpAvgRevenue', header: 'DFP avg. revenue'},
    {key: 'dpAvgRevenue', header: 'Partner avg. revenue'},
  ];

  columnHeaderMap = {};

  visibleColumns = this.columns.map(c => c.key);

  dateSelector = {
    dateFrom: MomentDateUtils.today(),
    dateTo: MomentDateUtils.today(),
    maxDate: MomentDateUtils.today(),
    allowedDateRangeTypes: [
      DateRangeType.TODAY,
      DateRangeType.YESTERDAY,
      DateRangeType.THIS_WEEK,
      DateRangeType.LAST_7_DAYS
    ],
    onDateRangeChanged: (event: DateRangeHolder) => {
      this.dateSelector.dateFrom = event.dateFrom;
      this.dateSelector.dateTo = event.dateTo;
      this.loadDiscrepanciesData();
    },
  };


  constructor(public dialogRef: MatDialogRef<KnMainTableDiscrepanciesDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: {
                dateFrom: string,
                dateTo: string,
                adUnitName: string,
                adUnitId: string,
                bidder: boolean,
              },
              private adStatsDiscrepanciesService: AdStatsDiscrepanciesService,
              private gss: GlobalSpinnerService) {
    this.data = data;
    if (this.data.dateFrom) {
      // prevent initial range to be bigger than 7 days
      const range = MomentDateUtils.getDurationInDays(data.dateFrom, data.dateTo);
      this.dateSelector.dateFrom = (range > 6.5) ? MomentDateUtils.nDaysFromDate(data.dateTo, -6) : data.dateFrom;
      this.dateSelector.dateTo = data.dateTo;
    }

    if (this.data.bidder) {
      this.columns.forEach(c => c.header = c.header.replace(/DFP/g, 'SB'));
    }
    this.columnHeaderMap = this.columns.reduce((prev, curr) => {
      prev[curr.key] = curr.header;
      return prev;
    }, {});

  }


  ngOnInit(): void {
    // set default sorting
    this.sort.sort(<MatSortable>({id: 'dfpRevenue', start: 'desc'}));
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.loadDiscrepanciesData();
  }

  loadDiscrepanciesData() {
    const requestData = <DiscrepanciesRequest> {
      adUnitIds: [this.data.adUnitId],
      dateFrom: this.dateSelector.dateFrom,
      dateTo: this.dateSelector.dateTo,
      includeAvgData: true,
      activeUnits: this.activeUnitsFilter,
      dataSource: this.data.bidder ? DiscrepancyReportRequestDataSource.SB : DiscrepancyReportRequestDataSource.DFP
    };

    this.gss.showLoader();
    this.loading = true;
    this.adStatsDiscrepanciesService.discrepancies(requestData).subscribe(res => {
      this.dataSource.data = this.prepareData(res);
      this.loading = false;
      this.gss.hideLoader();
    }, () => {
      this.loading = false;
      this.gss.hideLoader();
    });
  }

  prepareData(data: DiscrepanciesData[]): DiscrepanciesData[] {
    return data.map(item => {
      if (item.demandPartner === 'Adx') {
        item.dfpRevenue = item.dpRevenue;
      }

      return item;
    });
  }

  getTotal(columnName: string) {
    return this.dataSource.data.reduce((sum, currentValue) => {
      // exclude Adx partner from calculation
      if (currentValue.demandPartner === 'Adx') {
        return sum;
      } else {
        const value = Number(currentValue[columnName]);
        const correctValue = isNaN(value) ? 0 : value;
        return sum + correctValue;
      }
    }, 0);
  }

  getPercentageTotal(column1: string, column2: string) {
    const column1Total = this.getTotal(column1);
    const column2Total = this.getTotal(column2);

    return (column1Total - column2Total) / column1Total * 100;
  }

}
